import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaBook,
  FaClipboardList,
  FaBookOpen,
  FaCheckCircle,
  FaEye,
  FaBookReader,
  FaFileAlt,
} from "react-icons/fa";
import dayjs from "dayjs";
import academicStyles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import styles from "./Assessmentcreate.module.css";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import {
  CustomButton,
  CustomTextInput,
  CustomDatePicker,
} from "../../../../components";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];

const AssessmentView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useSelector((state) => state.language || {});
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";

  const assessmentId = location?.state?.id || "";

  // ==================== STATE ====================
  const [loading, setLoading] = useState(false);
  const [assessmentInfo, setAssessmentInfo] = useState({});
  const [questions, setQuestions] = useState([]);
  const [students, setStudents] = useState([]);
  const [pdfUrl, setPdfUrl] = useState("");

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const getFilterValue = useCallback(
    (id) => {
      const filter = columnFilters.find((c) => c.id === id);
      return filter?.value || "";
    },
    [columnFilters],
  );

  // ==================== FETCH ASSESSMENT VIEW ====================
  // Remembers which book path the PDF was already loaded for, so
  // pagination/filter refetches don't re-download the blob every time
  const loadedPdfPathRef = useRef("");

  const fetchAssessmentView = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const payload = {
        id: assessmentId,
        school_id: school_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        student_name: getFilterValue("student_name"),
        submitted_date: getFilterValue("submitted_date"),
        marks_obtained: getFilterValue("marks_display"),
        status: getFilterValue("display_status"),
      };

      const response = await AxiosInstance.post(
        `/book/assessment/view`,
        payload,
      );

      if (response?.data?.status) {
        const data = response.data.data || {};
        const info = data.assessment_info || {};
        setAssessmentInfo(info);
        setQuestions(data.questions || []);
        setStudents(data.students || []);
        setRowCount(data.pagination?.total_records || 0);

        if (
          info?.uploaded_file_path &&
          loadedPdfPathRef.current !== info.uploaded_file_path
        ) {
          loadedPdfPathRef.current = info.uploaded_file_path;
          const rawPath = info.uploaded_file_path;
          const isBlobPath =
            !/^https?:\/\//i.test(rawPath) &&
            !rawPath.startsWith("/") &&
            rawPath.includes("/Upload/");
          if (isBlobPath) {
            // Azure blob book — fetch through the authenticated
            // /content/image endpoint and show via an object URL
            try {
              const pdfRes = await AxiosInstance.get("/content/image", {
                params: { path: rawPath },
                responseType: "blob",
              });
              setPdfUrl(
                URL.createObjectURL(
                  new Blob([pdfRes.data], { type: "application/pdf" }),
                ),
              );
            } catch (err) {
              console.error("Error loading book PDF from blob:", err);
              setPdfUrl("");
            }
          } else if (/^https?:\/\//i.test(rawPath)) {
            setPdfUrl(rawPath);
          } else {
            const normalizedPath = rawPath.replace(/^\/+/, "");
            setPdfUrl(`${IMAGE_BASE_URL}${normalizedPath}`);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching assessment view:", error);
    } finally {
      setLoading(false);
    }
  }, [assessmentId, school_id, pagination.pageIndex,   // ✅ watch primitives, not the whole object
    pagination.pageSize, getFilterValue]);

  useEffect(() => {
    if (assessmentId) {
      fetchAssessmentView();
    }
  }, [fetchAssessmentView]);

  // ==================== HANDLERS ====================
  const handleCancel = () => {
    navigate("/user/assessmentList");
  };

  const handleViewResult = (row) => {
    navigate("/user/viewAssessmentResult", {
      state: {
        assessment_id: assessmentId,
        student_id: row?.student_id || "",
        mode: "result",
        data: row,
      },
    });
  };

  const handleCreateRemedialAssessment = (row) => {
    navigate(`/user/remedialCreate`, {
      state: {
        student_id: row.student_id,
        assessment_id: assessmentId,
        type: "remedial",
        data: row,
      },
    });
  };

  const handleCreateStudyMaterial = (row, bookId) => {

    if (row.is_studymaterial == 0) {
      navigate(`/user/studyMaterialCreate`, {
        state: {
          student_id: row.student_id,
          assessment_id: row.assessment_def_id,
          data: row,
          book_id: row.book_id,
        },
      });
    } else {
      navigate(`/user/ViewStudyMaterial`, {
        state: {
          student_id: row.student_id,
          assessment_id: row.assessment_def_id,
          book_id: assessmentInfo.book_id,
          data: row,
        },
      });
    }

  };

  const handleColumnFiltersChange = (filters) => {
    setColumnFilters(filters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // ==================== STATUS BADGE ====================
  const renderStatusBadge = (status) => {
    const normalized = (status || "").toLowerCase();
    let className = academicStyles.statusBadge;

    if (normalized === "pass") {
      className = `${academicStyles.statusBadge} ${academicStyles.statusApproved}`;
    } else if (normalized === "fail") {
      className = `${academicStyles.statusBadge} ${academicStyles.statusRejected}`;
    } else if (normalized === "pending") {
      className = `${academicStyles.statusBadge} ${academicStyles.statusSubmitted}`;
    }

    return <span className={className}>{status || "-"}</span>;
  };

  // ==================== ACTIONS RENDERER ====================
  const renderActions = (row) => {
    const status = (row.display_status || "").toLowerCase();

    if (status === "pending") {
      return (
        <div className={academicStyles.actionGroup}>
          <button
            className={`${academicStyles.viewBtn} ${academicStyles.viewBtnDisabled}`}
            disabled
            title={t?.pending || "Pending"}
          >
            <FaEye />
          </button>
        </div>
      );
    }

    if (status === "pass") {
      return (
        <div className={academicStyles.actionGroup}>
          <button
            className={academicStyles.viewBtn}
            onClick={() => handleViewResult(row)}
            title={t?.viewResult || "View Result"}
          >
            <FaEye />
          </button>
        </div>
      );
    }

    if (status === "fail") {
      const isRemedialDisabled = Number(row.is_pass) === 1;

      return (
        <div className={academicStyles.actionGroup}>
          <button
            className={academicStyles.viewBtn}
            onClick={() => handleViewResult(row)}
            title={t?.viewResult || "View Result"}
          >
            <FaEye />
          </button>
          <button
            className={`${academicStyles.viewBtn} ${isRemedialDisabled ? academicStyles.viewBtnDisabled : ""
              }`}
            onClick={
              isRemedialDisabled
                ? undefined
                : () => handleCreateRemedialAssessment(row)
            }
            disabled={isRemedialDisabled}
            title={
              isRemedialDisabled
                ? t?.remedialAlreadyCreated || "Remedial Already Created"
                : t?.createRemedialAssessment || "Create Remedial Assessment"
            }
          >
            <FaBookReader />
          </button>
          <button
            className={academicStyles.viewBtn}
            onClick={() => handleCreateStudyMaterial(row, assessmentInfo.book_id)}
            title={t?.createStudyMaterial || "Create Study Material"}
          >
            <FaFileAlt />
          </button>
        </div>
      );
    }

    return null;
  };

  // ==================== SINGLE DATE FILTER ====================
  const SingleDateFilter = ({ column, onDateChange }) => {
    const filterValue = column.getFilterValue();
    const [localDate, setLocalDate] = useState(filterValue || "");

    useEffect(() => {
      setLocalDate(filterValue || "");
    }, [filterValue]);

    const dateValue = localDate ? dayjs(localDate) : null;

    const handleChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalDate(formatted);
      // Keep MRT's columnFilters in sync (so chips/badges update and the
      // existing handleColumnFiltersChange resets pagination).
      column.setFilterValue(formatted || undefined);

      if (typeof onDateChange === "function") {
        onDateChange(formatted);
      }
    };

    return (
      <div className={academicStyles.dateRangeFilter}>
        <CustomDatePicker
          name={`${column.id}_date`}
          value={dateValue}
          onChange={handleChange}
          placeholder={t?.submittedDate || "Submitted Date"}
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          className={academicStyles.dateRangePicker}
        />
      </div>
    );
  };

  // ==================== TABLE COLUMNS ====================
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: t?.labels?.serialNo || "S.No",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "student_name",
        header: t?.studentName || "Student Name",
        size: 200,
      },
      {
        accessorKey: "submitted_date",
        header: t?.submittedDate || "Submitted Date",
        size: 200,
        enableColumnFilter: true,
        Filter: ({ column }) => <SingleDateFilter column={column} />,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          // API returns the literal string "-" for pending submissions —
          // bail out before handing it to dayjs so we don't render "Invalid Date".
          if (!v || v === "-") return "-";
          const parsed = dayjs(v);
          return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
        },
      },
      {
        accessorKey: "marks_display",
        header: t?.marksObtained || "Marks Obtained",
        size: 150,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "display_status",
        header: t?.status || "Status",
        size: 130,
        Cell: ({ cell }) => renderStatusBadge(cell.getValue()),
      },
      {
        id: "actions",
        header: t?.actions || "Action",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => renderActions(row.original),
      },

    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, assessmentInfo?.book_id, pagination.pageIndex, pagination.pageSize],
  );

  // ==================== DERIVED VALUES ====================
  const formData = useMemo(
    () => ({
      bookName: assessmentInfo?.book_name || "",
      className: assessmentInfo?.class_name || "",
      section: assessmentInfo?.section_id || "",
      medium: assessmentInfo?.medium || "",
      topic: assessmentInfo?.topic || "",
      sub_topic: assessmentInfo?.sub_topic || "",
      start_page: assessmentInfo?.start_page || "",
      end_page: assessmentInfo?.end_page || "",
      num_questions: assessmentInfo?.num_questions || "",
      difficulty_level: assessmentInfo?.difficulty_level || "",
      mcq_options: assessmentInfo?.mcq_options_count || 4,
      pass_percentage: assessmentInfo?.pass_percentage || "",
    }),
    [assessmentInfo],
  );

  return (
    <div>
      {/* ========== CARD 1: Book Details + Generate Assessment ========== */}
      <div className={academicStyles.container}>
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaBook />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.bookDetails || "Book Details"}
          </h1>
        </div>

        {/* ROW 1 — Book / Class / Section / Medium */}
        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.book || "Book"}
              name="bookName"
              value={formData.bookName}
              placeholder={t?.selectBook || "Select Book"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.class || "Class"}
              name="className"
              value={formData.className}
              placeholder={t?.selectClass || "Class"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.section || "Section"}
              name="section"
              value={formData.section}
              placeholder={t?.selectSection || "Section"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.medium || "Medium"}
              name="medium"
              value={formData.medium}
              placeholder={t?.selectMedium || "Medium"}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* ROW 2 — Topic / Sub Topic / End Page / Start Page */}
        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.topic || "Topic"}
              name="topic"
              value={formData.topic}
              placeholder={t?.selectTopicPlaceholder || "Select Topic"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.subTopic || "Sub Topic"}
              name="sub_topic"
              value={formData.sub_topic}
              placeholder={t?.selectSubTopic || "Select Sub Topic"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.startPageLabel || "Start Page"}
              name="start_page"
              value={formData.start_page}
              placeholder={t?.enterStartPage || "Enter Start Page"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.endPageLabel || "End Page"}
              name="end_page"
              value={formData.end_page}
              placeholder={t?.enterEndPage || "Enter End Page"}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* ========== Generate Assessment Section ========== */}
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.generateAssessment || "Generate Assessment"}
          </h1>
        </div>

        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.numberOfQuestion || "Number of Question"}
              name="num_questions"
              value={formData.num_questions}
              placeholder={
                t?.enterNumberOfQuestions || "Enter Number of Questions"
              }
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.difficultyLevel || "Difficulty Level"}
              name="difficulty_level"
              value={formData.difficulty_level}
              placeholder={t?.selectDifficulty || "Select Difficulty"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.mcqOptionsCount || "MCQ Options Count"}
              name="mcq_options"
              value={formData.mcq_options}
              placeholder={t?.enterOptionsCount || "Enter Options Count"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.passPercentage || "Pass Percentage"}
              name="pass_percentage"
              value={formData.pass_percentage}
              placeholder={t?.enterPassPercentage || "Enter Pass Percentage"}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>

      {/* ========== PDF + PREVIEW ========== */}
      <div className={`row mt-3 ${styles.previewRow}`}>
        <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderIcon}>
                <FaBookOpen />
              </div>
              <h1 className={styles.panelHeaderTitle}>
                {t?.bookPdfTitle || "Book PDF"}
              </h1>
            </div>
            <div className={styles.pdfBody}>
              <PdfViewer
                file={pdfUrl}
                fromPage={Number(formData.start_page) || undefined}
                toPage={Number(formData.end_page) || undefined}
                fallbackMessage={
                  t?.pdfFallbackMessage || "PDF will appear here"
                }
                enableFullscreen
              />
            </div>
          </div>
        </div>

        <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderIcon}>
                <FaClipboardList />
              </div>
              <h1 className={styles.panelHeaderTitle}>
                {t?.previewAssessment || "Preview Assessment"}
              </h1>
            </div>

            <div className={styles.previewBody}>
              {questions.length === 0 ? (
                <div className={styles.emptyState}>
                  {t?.noQuestionsGenerated || "No questions generated yet"}
                </div>
              ) : (
                questions.map((q, qIdx) => {
                  const optionCount = Number(formData.mcq_options) || 4;
                  const correct = (q.correct_answer || "")
                    .toString()
                    .toLowerCase();

                  return (
                    <div key={q.id || qIdx} className={styles.questionBlock}>
                      <div className={styles.questionText}>
                        {qIdx + 1}. {q.assessment_question}
                      </div>

                      <div className={styles.optionsGrid}>
                        {OPTION_LETTERS.slice(0, optionCount).map((letter) => {
                          const optKey = `option_${letter}`;
                          const optValue = q[optKey];
                          if (optValue === undefined || optValue === null)
                            return null;

                          const isCorrect = correct === letter;

                          return (
                            <div key={letter} className={styles.optionCol}>
                              <div
                                className={`${styles.optionCard} ${isCorrect ? styles.optionCardCorrect : ""
                                  }`}
                              >
                                <span className={styles.radioWrapper}>
                                  <input
                                    type="radio"
                                    name={`q-${qIdx}`}
                                    checked={isCorrect}
                                    readOnly
                                    className={styles.radioInput}
                                  />
                                  <span
                                    className={`${styles.radioMark} ${isCorrect ? styles.radioMarkChecked : ""
                                      }`}
                                  >
                                    {isCorrect && (
                                      <span className={styles.radioInner} />
                                    )}
                                  </span>
                                </span>
                                <span className={styles.optionText}>
                                  {letter}) {optValue}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {q.explanation && (
                        <div className={styles.explanationPanel}>
                          <FaCheckCircle className={styles.explanationIcon} />
                          <div className={styles.explanationContent}>
                            <div className={styles.explanationCorrectAnswer}>
                              {correct}) {q[`option_${correct}`] || ""}
                            </div>
                            <div className={styles.explanationText}>
                              {t?.explanation || "Explanation"} :{" "}
                              {q.explanation}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ========== Students List Section ========== */}
      <div
        className={`${academicStyles.container} ${academicStyles.sectionSpacing}`}
      >
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.studentsList || "Students List"}
          </h1>
        </div>

        <CustomMRTTable
          columns={columns}
          data={students}
          loading={loading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          enableColumnFilters={true}
          enableGlobalFilter={false}
          hideGlobalSearch={false}
        />
      </div>

      {/* ========== Footer — Back Button ========== */}
      <div
        className={`${styles.buttonRowCenter} ${academicStyles.sectionSpacing}`}
      >
        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.cancelButton}
          fullWidth={false}
          onClick={handleCancel}
        >
          {t?.back || "Back"}
        </CustomButton>
      </div>
    </div>
  );
};

export default AssessmentView;
